import React, { useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';


const HREmployees = () => {
  const baseUrl = API_BASE_URL;
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const token = Cookies.get('token');
        const [empRes, depRes, ccRes] = await Promise.all([
          axios.get(`${baseUrl}/api/department-employees/`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseUrl}/api/departments/`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${baseUrl}/api/course-centers/`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        setEmployees(empRes.data || []);
        setDepartments(depRes.data || []);
        setCostCenters(ccRes.data || []);
      } catch (err) {
        console.error('Failed to fetch department employees:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [baseUrl]);

  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return employees;
    const s = search.toLowerCase();
    return employees.filter(emp =>
      emp.first_name?.toLowerCase().includes(s) ||
      emp.last_name?.toLowerCase().includes(s) ||
      emp.national_id?.toLowerCase().includes(s) ||
      emp.position?.toLowerCase().includes(s)
    );
  }, [employees, search]);

  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
  const paginatedEmployees = useMemo(() => {
    return filteredEmployees.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  const handleSearchChange = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage(p => Math.max(1, p - 1));
  const handleNextPage = () => setPage(p => Math.min(totalPages, p + 1));

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Department Employees</h4>
              <p className="text-muted mb-0 fs-13">View and manage department employees and their goals</p>
            </div>
          </div>

          {/* Search Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-6">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control ps-5"
                      placeholder="Search by name, national ID, or position..."
                      value={search}
                      onChange={handleSearchChange}
                      disabled={loading}
                    />
                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="card shadow-sm mb-4">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Name</th>
                    <th>Surname</th>
                    <th>National ID</th>
                    <th>Position</th>
                    <th style={{ width: '220px' }}>Progress</th>
                    <th className="text-end pe-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted fs-13 mt-2 mb-0">Loading employees...</p>
                      </td>
                    </tr>
                  ) : paginatedEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        <i className="ti ti-users-minus fs-36 d-block mb-2 text-secondary opacity-50" />
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    paginatedEmployees.map(emp => {
                      const progressVal = Number(emp.performance_progress || 0);
                      const barBg = emp.performance_indicator || '#3550DC';

                      return (
                        <tr key={emp.id}>
                          <td className="ps-3 fw-semibold">{emp.first_name || '—'}</td>
                          <td>{emp.last_name || '—'}</td>
                          <td>{emp.national_id || '—'}</td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {emp.position || '—'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: '18px', backgroundColor: '#eef2ff' }}>
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{
                                    width: `${progressVal.toFixed(2)}%`,
                                    backgroundColor: barBg
                                  }}
                                  aria-valuenow={progressVal}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                >
                                  <span className="fs-11 fw-bold">{progressVal.toFixed(1)}%</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end pe-3">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <Link
                                to={`/hr/department-employee/${emp.id}/goals`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-icon btn-light"
                                title="View Goals"
                              >
                                <i className="ti ti-eye text-primary" />
                              </Link>

                              <Link
                                to={`/hr/employee-goals/${emp.id}/reviews`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-outline-primary ms-1"
                                title="Review Goals"
                              >
                                Review Goals
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredEmployees.length > 0 && (
              <div className="card-footer d-flex align-items-center justify-content-between py-2 px-3 border-top">
                <span className="text-muted fs-13">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, filteredEmployees.length)} to {Math.min(page * rowsPerPage, filteredEmployees.length)} of {filteredEmployees.length} employees
                </span>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className="btn btn-sm btn-light"
                  >
                    <i className="ti ti-chevron-left me-1" />
                    Prev
                  </button>
                  <span className="fs-13 fw-semibold px-2">
                    Page {page} of {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={page === totalPages || totalPages === 0}
                    className="btn btn-sm btn-light"
                  >
                    Next
                    <i className="ti ti-chevron-right ms-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default HREmployees;
